/**
 * Phase 94 — "+ Add category" quick-add for Expenses. Appends a brand-new
 * Department/Category/Sub-category tuple to the shared taxonomy (persists
 * for everyone) without leaving the Entries Ledger.
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function QuickAddCategoryDialog({ open, onOpenChange, departments, onAdded }) {
  const [form, setForm] = useState({ department: "", category: "", sub_category: "", cost_type: "NA" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.department || !form.category.trim() || !form.sub_category.trim()) {
      toast.error("Department, Category and Sub-category are all required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/finance/categories/quick-add", form);
      toast.success(`Added "${form.category} → ${form.sub_category}"`);
      onAdded?.(form);
      setForm({ department: "", category: "", sub_category: "", cost_type: "NA" });
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="quick-add-category-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Plus size={16} /> Add category</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Department *</Label>
            <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="quick-cat-department"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Category *</Label>
            <Input className="rounded-sm mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="quick-cat-category" />
          </div>
          <div>
            <Label className="label-overline">Sub-category *</Label>
            <Input className="rounded-sm mt-1" value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} data-testid="quick-cat-subcategory" />
          </div>
          <div>
            <Label className="label-overline">Cost Type</Label>
            <Input className="rounded-sm mt-1" placeholder="NA" value={form.cost_type} onChange={(e) => setForm({ ...form, cost_type: e.target.value })} data-testid="quick-cat-cost-type" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="quick-cat-save">
            {saving ? "Adding…" : "Add category"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
